import { useEffect, useRef } from 'react';
import i18n from './index';
import { LANGUAGE_STORAGE_KEY, SUPPORTED_LANGUAGE_CODES } from './config';

/**
 * Keeps the UI language and the signed-in account's saved language in step (medopd-docs 106).
 *
 *  - On sign-in, a language saved on the ACCOUNT wins: it is applied (and remembered on this device), so
 *    the person sees their language on any device. On a shared reception PC this is also what stops one
 *    person's choice overwriting a colleague's.
 *  - An account with no saved language adopts the language currently showing, so it is set the first time.
 *  - Switching language while signed in saves it to the account.
 *
 * Saving is best-effort: a failed request leaves the UI in the chosen language for this session.
 *
 * @param accountId    the signed-in account's id, or null when signed out
 * @param savedLanguage the language saved on that account (from /me), if any
 * @param save         persists a language to that account
 */
export function useAccountLanguageSync(
  accountId: string | null | undefined,
  savedLanguage: string | null | undefined,
  save: (language: string) => Promise<unknown>,
): void {
  // The language the account is known to hold; changes equal to it are not re-saved.
  const synced = useRef<string | null>(null);
  const saveRef = useRef(save);
  saveRef.current = save;

  // Sign-in (or a different account): reconcile once.
  useEffect(() => {
    if (!accountId) { synced.current = null; return; }
    const saved = savedLanguage && SUPPORTED_LANGUAGE_CODES.includes(savedLanguage) ? savedLanguage : null;
    const current = i18n.resolvedLanguage || i18n.language;
    if (saved) {
      synced.current = saved;
      if (saved !== current) {
        void i18n.changeLanguage(saved);
        try { localStorage.setItem(LANGUAGE_STORAGE_KEY, saved); } catch { /* storage blocked */ }
      }
    } else if (current && SUPPORTED_LANGUAGE_CODES.includes(current)) {
      synced.current = current;
      saveRef.current(current).catch(() => { synced.current = null; });
    }
  }, [accountId, savedLanguage]);

  // Later switches while signed in are saved.
  useEffect(() => {
    if (!accountId) return undefined;
    const onChange = (lng: string) => {
      const code = lng.split('-')[0];
      if (!SUPPORTED_LANGUAGE_CODES.includes(code) || code === synced.current) return;
      synced.current = code;
      saveRef.current(code).catch(() => { synced.current = null; });
    };
    i18n.on('languageChanged', onChange);
    return () => { i18n.off('languageChanged', onChange); };
  }, [accountId]);
}
